import { useState, useEffect } from "react";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import ModuleShell from "@/components/ModuleShell";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Clock, Package, CalendarBlank } from "@phosphor-icons/react";

function fmtMin(m) {
  const n = parseInt(m || 0, 10);
  const h = Math.floor(n / 60);
  const mm = n % 60;
  if (h === 0) return `${mm}min`;
  if (mm === 0) return `${h}h`;
  return `${h}h ${mm}min`;
}

function todayIso() { return new Date().toISOString().slice(0, 10); }
function firstOfMonth(d = new Date()) { return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); }
function firstOfPrevMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10);
}
function lastOfPrevMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10);
}
function firstOfYear() { return `${new Date().getFullYear()}-01-01`; }

const SHORTCUTS = [
  { key: "current-month", label: "Mese corrente", getRange: () => ({ from: firstOfMonth(), to: todayIso() }) },
  { key: "prev-month", label: "Mese precedente", getRange: () => ({ from: firstOfPrevMonth(), to: lastOfPrevMonth() }) },
  { key: "current-year", label: "Anno corrente", getRange: () => ({ from: firstOfYear(), to: todayIso() }) },
  { key: "all", label: "Tutto", getRange: () => ({ from: "", to: "" }) },
];

export default function MinutePackageReportModule() {
  useDocumentTitle("Modulo · Riepilogo Pacchetto Minuti");
  const [customer, setCustomer] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState("all");
  const [scope, setScope] = useState("current");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeShortcut, setActiveShortcut] = useState(null);
  const [loadingPkgs, setLoadingPkgs] = useState(false);

  useEffect(() => {
    if (!customer) { setPackages([]); setSelectedPkg("all"); return; }
    setLoadingPkgs(true);
    api.get(`/customers/${customer.id}/services`).then(({ data }) => {
      const pkgs = data.filter((s) => s.category === "pacchetto_minuti");
      setPackages(pkgs);
      setSelectedPkg("all");
    }).catch(() => {}).finally(() => setLoadingPkgs(false));
  }, [customer]);

  const applyShortcut = (sc) => {
    const r = sc.getRange();
    setDateFrom(r.from);
    setDateTo(r.to);
    setActiveShortcut(sc.key);
  };

  const buildPayload = (customerId) => ({
    customer_id: customerId,
    service_id: selectedPkg && selectedPkg !== "all" ? selectedPkg : null,
    scope,
    date_from: dateFrom || null,
    date_to: dateTo || null,
  });

  const hasDateFilter = dateFrom || dateTo;

  return (
    <ModuleShell
      title="Riepilogo Pacchetto Minuti"
      subtitle="Situazione minuti residui + storico interventi (filtrabile per periodo)"
      icon={Clock}
      endpoint="/modules/minute-package-report/pdf"
      filenamePrefix="pacchetto-minuti"
      emailSubject={() => "Riepilogo Pacchetto Minuti - Diotech"}
      emailBody={(c) => `Gentile ${c.surname} ${c.name},\n\nin allegato trova il riepilogo del pacchetto minuti con l'elenco degli interventi registrati.`}
      onCustomerChange={setCustomer}
      validate={() => {
        if (!packages || packages.length === 0) return "Questo cliente non ha pacchetti minuti attivi";
        if (dateFrom && dateTo && dateFrom > dateTo) return "Data inizio successiva a data fine";
        return null;
      }}
      buildPayload={buildPayload}
    >
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">// PACCHETTI MINUTI</div>

      {!customer && (
        <div className="text-sm text-muted-foreground/80 font-mono border border-dashed border-border rounded-md p-6 text-center">
          — Seleziona un cliente per vedere i suoi pacchetti minuti —
        </div>
      )}

      {customer && loadingPkgs && (
        <div className="text-sm text-muted-foreground">Caricamento pacchetti…</div>
      )}

      {customer && !loadingPkgs && packages.length === 0 && (
        <div className="text-sm text-rose-600 dark:text-rose-400 font-mono border border-rose-500/40 bg-rose-500/5 rounded-md p-4">
          Nessun pacchetto minuti attivo per questo cliente. Vendi un pacchetto REMOTE dalla scheda cliente.
        </div>
      )}

      {customer && !loadingPkgs && packages.length > 0 && (
        <div className="space-y-4">
          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest">Pacchetto da includere nel report</Label>
            <Select value={selectedPkg} onValueChange={setSelectedPkg}>
              <SelectTrigger className="mt-1.5" data-testid="minute-pkg-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i pacchetti ({packages.length})</SelectItem>
                {packages.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} · {fmtMin(p.minutes_remaining || 0)} residui su {fmtMin(p.minutes_total || 0)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date range filter */}
          <div className="border border-border/60 rounded-md p-3 bg-secondary/20 space-y-2">
            <div className="flex items-center gap-2">
              <CalendarBlank size={14} className="text-primary" />
              <Label className="font-mono text-[10px] uppercase tracking-widest">Filtro periodo interventi</Label>
              {hasDateFilter && (
                <button
                  type="button"
                  onClick={() => { setDateFrom(""); setDateTo(""); setActiveShortcut(null); }}
                  className="ml-auto text-[10px] font-mono text-muted-foreground hover:text-foreground underline"
                  data-testid="clear-date-filter"
                >
                  reset
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="date-shortcuts">
              {SHORTCUTS.map((sc) => (
                <Button
                  key={sc.key}
                  type="button"
                  size="sm"
                  variant={activeShortcut === sc.key ? "default" : "outline"}
                  onClick={() => applyShortcut(sc)}
                  className="rounded-full h-7 text-xs font-mono"
                  data-testid={`date-shortcut-${sc.key}`}
                >
                  {sc.label}
                </Button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label className="font-mono text-[9px] uppercase text-muted-foreground">Da</Label>
                <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setActiveShortcut(null); }} className="mt-1 h-8 text-xs" data-testid="date-from-input" />
              </div>
              <div>
                <Label className="font-mono text-[9px] uppercase text-muted-foreground">A</Label>
                <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setActiveShortcut(null); }} className="mt-1 h-8 text-xs" data-testid="date-to-input" />
              </div>
            </div>
            {hasDateFilter && (
              <div className="text-[10px] text-muted-foreground font-mono">
                Il PDF includerà solo gli interventi nel periodo selezionato.
              </div>
            )}
          </div>

          <div>
            <Label className="font-mono text-[10px] uppercase tracking-widest mb-2 block">Contenuto del report</Label>
            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer ${scope === "current" ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/40"}`} data-testid="scope-current-option">
                <input
                  type="radio"
                  name="scope"
                  value="current"
                  checked={scope === "current"}
                  onChange={() => setScope("current")}
                  className="mt-1 accent-primary"
                  data-testid="scope-current-radio"
                />
                <div>
                  <div className="text-sm font-medium">Solo utilizzo dall'ultima ricarica</div>
                  <div className="text-xs text-muted-foreground">Situazione attuale + interventi del ciclo corrente. Ideale per il cliente.</div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer ${scope === "full" ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/40"}`} data-testid="scope-full-option">
                <input
                  type="radio"
                  name="scope"
                  value="full"
                  checked={scope === "full"}
                  onChange={() => setScope("full")}
                  className="mt-1 accent-primary"
                  data-testid="scope-full-radio"
                />
                <div>
                  <div className="text-sm font-medium">Tutti i movimenti (cronologia completa)</div>
                  <div className="text-xs text-muted-foreground">Include cronologia rinnovi con prezzi + archivio cicli precedenti. Utile per fine anno.</div>
                </div>
              </label>
            </div>
          </div>

          <div className="space-y-2" data-testid="pkg-preview-list">
            {packages
              .filter((p) => selectedPkg === "all" || p.id === selectedPkg)
              .map((p) => {
                const total = p.minutes_total || 0;
                const used = p.minutes_used || 0;
                const remaining = p.minutes_remaining || 0;
                const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
                return (
                  <div key={p.id} className="border border-border/60 rounded-md p-3">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <Package size={14} weight="duotone" className="text-primary" />
                        <div className="font-medium text-sm">{p.name}</div>
                      </div>
                      <div className="text-xs font-mono">
                        <span className={remaining <= 0 ? "text-rose-600 dark:text-rose-400 font-semibold" : "font-semibold"}>{fmtMin(remaining)}</span>
                        <span className="text-muted-foreground"> / {fmtMin(total)}</span>
                      </div>
                    </div>
                    <div className="h-1.5 bg-secondary rounded overflow-hidden">
                      <div className={`h-full ${remaining <= 0 ? "bg-rose-500" : pct > 80 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </ModuleShell>
  );
}
